import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { 
  Loader2,
  FileText,
  Edit,
  RefreshCw,
  CheckCircle,
  Circle,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const sectionLabels = {
  jugar: { ca: 'Jugar', icon: '⛳' },
  iniciacio: { ca: 'Iniciació / Escola', icon: '🎓' },
  drivingRange: { ca: 'Camp de Pràctiques', icon: '🏌️' },
  restaurant: { ca: 'Restaurant', icon: '🍽️' },
  allotjament: { ca: 'Allotjament', icon: '🏨' },
  correspondencies: { ca: 'Correspondències', icon: '🤝' },
  equip: { ca: 'Equip', icon: '👥' }
};

const sectionUrls = {
  jugar: '/jugar',
  iniciacio: '/iniciacio',
  drivingRange: '/driving-range',
  restaurant: '/restaurant',
  allotjament: '/allotjament',
  correspondencies: '/correspondencies',
  equip: '/equip'
};

export const AdminSections = () => {
  const navigate = useNavigate();
  const { isAdmin, authFetch } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [sections, setSections] = useState([]);

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/section-pages`, {  });
      if (response.ok) {
        const data = await response.json();
        setSections(data.sections || []);
      }
    } catch (error) {
      console.error('Failed to fetch sections:', error);
      toast.error('Error carregant seccions');
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    setInitializing(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/section-content/init`, {
        method: 'POST',
        
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchSections();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error inicialitzant');
      }
    } catch (error) {
      toast.error('Error inicialitzant seccions');
    } finally {
      setInitializing(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-sections">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A4D2E] flex items-center gap-2">
              <FileText className="w-6 h-6" />
              Pàgines de Secció
            </h1>
            <p className="text-[#666666] mt-1">
              Edita el contingut de les pàgines de secció (Jugar, Restaurant, etc.)
            </p>
          </div>
          
          {isAdmin && (
            <Button
              onClick={handleInitialize}
              disabled={initializing}
              variant="outline"
              className="gap-2"
            >
              {initializing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Inicialitzar contingut per defecte
            </Button>
          )}
        </div>

        {/* Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(sectionLabels).map(([key, info]) => {
            const section = sections.find(s => s.key === key);
            const hasContent = section && Object.values(section.languages || {}).some(l => l.exists);
            
            return (
              <div 
                key={key}
                className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden hover:border-[#1A4D2E] transition-colors"
              >
                {/* Hero preview */}
                {section?.hero_image && (
                  <div className="h-32 overflow-hidden">
                    <img 
                      src={section.hero_image} 
                      alt={info.ca}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                {/* Content */}
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-[#333333] flex items-center gap-2">
                      <span>{info.icon}</span>
                      {info.ca}
                    </h3>
                    {hasContent ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <Circle className="w-4 h-4 text-[#CCCCCC]" />
                    )}
                  </div>
                  
                  {/* Language status */}
                  <div className="flex gap-1">
                    {['ca', 'es', 'fr', 'en'].map(lang => {
                      const langData = section?.languages?.[lang];
                      const exists = langData?.exists;
                      
                      return (
                        <span
                          key={lang}
                          className={`px-2 py-0.5 text-xs font-medium rounded ${
                            exists 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-500'
                          }`}
                          title={exists ? `${lang.toUpperCase()}: Configurat` : `${lang.toUpperCase()}: Per defecte`}
                        >
                          {lang.toUpperCase()}
                        </span>
                      );
                    })}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/admin/sections/${key}`)}
                      className="flex-1 gap-2 bg-[#1A4D2E] hover:bg-[#153d25] text-white"
                      data-testid={`edit-${key}-btn`}
                    >
                      <Edit className="w-3 h-3" />
                      Editar
                    </Button>
                    <a
                      href={sectionUrls[key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-3 py-2 border border-[#E5E5E5] rounded text-sm text-[#666666] hover:border-[#1A4D2E] hover:text-[#1A4D2E] transition-colors"
                    >
                      <Eye className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSections;
